import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import "./game.css";
import { selectAdminToken } from "../../admin/sessionSlice";
import { fetchThemeData } from "../../admin/themeSlice";
import { getDvSourceImageUrl } from "../../functions/themeAssets";
import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";
import { lightenColor, darkenColor } from "../../functions/colorUtils";

const USER_DATA_KEY = "userData";
const DEFAULT_QUESTION_SECONDS = 30;
const DEFAULT_HINTS = 3;
const OPTION_KEYS = ["option_one", "option_two", "option_three", "option_four"];
const OPTION_LETTERS = ["A", "B", "C", "D"];
const HINT_VIDEO_EXT = ["mp4", "mov", "webm", "mkv", "avi"];
const HINT_AUDIO_EXT = ["mp3", "wav", "ogg", "m4a"];

function getQuestionHintMediaKind(filename) {
  const ext = String(filename || "").split(".").pop().toLowerCase();
  if (HINT_VIDEO_EXT.includes(ext)) return "video";
  if (HINT_AUDIO_EXT.includes(ext)) return "audio";
  if (ext) return "image";
  return null;
}

function readSessionUserData() {
  try {
    return JSON.parse(sessionStorage.getItem(USER_DATA_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function formatTime(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

/** Parse "MM:SS" (or "HH:MM:SS") back into total seconds for resuming the timer. */
function parseTimeToSeconds(value) {
  if (value == null) return 0;
  const parts = String(value).trim().split(":").map((n) => Number(n));
  if (parts.some((n) => Number.isNaN(n))) return 0;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 1) return parts[0];
  return 0;
}

function parseStoredBool(raw, fallback) {
  if (raw === undefined || raw === null || raw === "") return fallback;
  if (typeof raw === "boolean") return raw;
  const s = String(raw).trim().toLowerCase();
  if (s === "true" || s === "1" || s === "yes") return true;
  if (s === "false" || s === "0" || s === "no") return false;
  return fallback;
}

function ensureJsonArray(val) {
  if (Array.isArray(val)) return val;
  if (typeof val === "string" && val.trim()) {
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      /* not json */
    }
  }
  return [];
}

function ensureJsonObject(val) {
  if (val && typeof val === "object" && !Array.isArray(val)) return val;
  if (typeof val === "string" && val.trim()) {
    try {
      const parsed = JSON.parse(val);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed;
    } catch {
      /* not json */
    }
  }
  return {};
}

function shuffleArray(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Self-contained canvas confetti burst — no external dependency. */
function ConfettiBurst({ onDone }) {
  const canvasRef = useRef(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext("2d");
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const colors = ["#E25569", "#FB9946", "#FFD93D", "#6BCB77", "#4D96FF", "#FF6B9D", "#C77DFF"];
    const particles = [];

    [{ x: 0, y: canvas.height, dir: 1 }, { x: canvas.width, y: canvas.height, dir: -1 }].forEach((origin) => {
      for (let i = 0; i < 90; i += 1) {
        particles.push({
          x: origin.x,
          y: origin.y,
          vx: origin.dir * (Math.random() * 8 + 3) + (Math.random() - 0.5) * 6,
          vy: -(Math.random() * 14 + 6),
          w: Math.random() * 9 + 4,
          h: Math.random() * 6 + 3,
          color: colors[Math.floor(Math.random() * colors.length)],
          rotation: Math.random() * 360,
          spin: (Math.random() - 0.5) * 12,
          life: 1,
          gravity: 0.22,
        });
      }
    });

    let frameId;
    let ticks = 0;
    const maxTicks = 180;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        if (p.life <= 0) return;
        p.vy += p.gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.spin;
        p.life -= 0.007;
        ctx.save();
        ctx.globalAlpha = Math.max(p.life, 0);
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      });
      ticks += 1;
      if (ticks < maxTicks) {
        frameId = requestAnimationFrame(draw);
      } else {
        onDoneRef.current?.();
      }
    };

    frameId = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(frameId);
    };
  }, []);

  return <canvas ref={canvasRef} className="confetti-canvas" aria-hidden="true" />;
}

export default function GamePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData } = useSelector((state) => state.theme);
  const storedUserData = useMemo(() => readSessionUserData(), []);

  const userId = storedUserData?.userId || storedUserData?.userid || storedUserData?.id || "";
  const organizationId = storedUserData?.organizationId || "admin";
  const sessionId = storedUserData?.sessionId || "admin";
  const currentThemeName = themeData?.themename ?? themeData?.themeName ?? themeData?.themeId ?? "";

  // Theme data is normally already loaded during login — fetch defensively in case
  // this route is reached directly (refresh, deep link) before that's happened.
  useEffect(() => {
    if (!themeData && adminToken) dispatch(fetchThemeData({}));
  }, [themeData, adminToken, dispatch]);

  const rightPoints = Number(themeData?.points ?? 10) || 10;
  const wrongPoints = Number(themeData?.wrong_points ?? 0) || 0;
  const useTimeout = parseStoredBool(themeData?.use_timeout, true);
  const questionDurationSeconds = useMemo(() => {
    const m = Number(themeData?.question_timeout_minutes ?? 0) || 0;
    const s = Number(themeData?.question_timeout_seconds ?? 0) || 0;
    const total = m * 60 + s;
    return total > 0 ? total : DEFAULT_QUESTION_SECONDS;
  }, [themeData]);
  const hintsEnabled = parseStoredBool(themeData?.hint, true);
  const categoriesStatus = parseStoredBool(themeData?.categories_status, false);
  const shuffleQuestions = parseStoredBool(themeData?.shuffle_questions, true);
  const totalQuestionTarget = Math.max(1, Number(themeData?.total_question ?? 10) || 10);
  const categoryQuestionCounts = useMemo(
    () => ensureJsonObject(themeData?.categoryQuestions),
    [themeData]
  );

  // ---- question set ----
  const [rawQuestions, setRawQuestions] = useState([]);
  const [playQuestions, setPlayQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [questionsError, setQuestionsError] = useState("");

  // ---- stage/progress ----
  // The `stat` row is keyed by userId+organizationId+sessionId (no row id needed
  // for later calls), so resume position is tracked purely via question_count.
  const [progressLoaded, setProgressLoaded] = useState(false);
  const [score, setScore] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [questionCount, setQuestionCount] = useState(0);
  const [gameCompleted, setGameCompleted] = useState(false);
  const gameCompletedRef = useRef(false);
  useEffect(() => {
    gameCompletedRef.current = gameCompleted;
  }, [gameCompleted]);

  // Refs mirroring the latest values so the unmount-save effect (empty deps) never closes over stale state.
  const latestRef = useRef({ questionCount: 0, score: 0, elapsed: 0 });
  useEffect(() => {
    latestRef.current = { questionCount, score, elapsed };
  }, [questionCount, score, elapsed]);

  // ---- current question / answering ----
  const currentIndex = Math.min(questionCount, playQuestions.length);
  const currentQuestion = playQuestions[currentIndex] || null;
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [revealState, setRevealState] = useState(null); // { correctId, wrongId }
  const [confettiActive, setConfettiActive] = useState(false);
  const [wrongFlash, setWrongFlash] = useState(false);
  const wrongFlashTimerRef = useRef(null);

  // ---- hints ----
  const [hintsLeft, setHintsLeft] = useState(DEFAULT_HINTS);
  const [revealedHints, setRevealedHints] = useState({});
  const [hintConfirmOpen, setHintConfirmOpen] = useState(false);
  const [hintDisplayOpen, setHintDisplayOpen] = useState(false);
  const [hintDisplayText, setHintDisplayText] = useState("");

  const [rulesOpen, setRulesOpen] = useState(false);

  // ---- per-question countdown ----
  const [questionSecondsLeft, setQuestionSecondsLeft] = useState(questionDurationSeconds);

  // ---- external report id ----
  const [reportId, setReportId] = useState(storedUserData?.reportId || storedUserData?.report_id || null);

  const [isMobileViewport, setIsMobileViewport] = useState(
    () => window.matchMedia("(max-width: 768px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = () => setIsMobileViewport(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const backgroundImage = useMemo(() => {
    if (!themeData) return "";
    const key = isMobileViewport
      ? themeData.game_background_mob || themeData.background_mob
      : themeData.game_background_desk || themeData.background_desk;
    return key ? getDvSourceImageUrl(key) : "";
  }, [themeData, isMobileViewport]);

  const themeVars = useMemo(() => {
    const textColor = themeData?.landing_page_title_color || themeData?.textcolor || "#000000";
    // Box Color now drives buttons/badges specifically (submit, hint, rules-help,
    // timer/points/hints pills). Question box + answer options each get their own
    // color, falling back to Box Color so themes saved before this split still
    // look the same.
    const boxColor = themeData?.box_color || "#1E3C72";
    const questionBoxColor = themeData?.question_box_color || boxColor;
    const optionBoxColor = themeData?.option_box_color || boxColor;
    const boxTextColor = themeData?.box_text_color || "#FFFFFF";
    const iconColor = themeData?.icon_color || "#ffd700";
    return {
      "--theme-text-color": textColor,
      "--theme-box-color": boxColor,
      "--theme-box-color-hover": lightenColor(boxColor),
      "--theme-box-color-dark": darkenColor(boxColor),
      "--theme-question-box-color": questionBoxColor,
      "--theme-option-box-color": optionBoxColor,
      "--theme-option-box-color-hover": lightenColor(optionBoxColor),
      "--theme-box-text-color": boxTextColor,
      "--theme-icon-color": iconColor,
    };
  }, [themeData]);

  // The <USER> wrapper renders its own header above this page's content in normal
  // flow, so a background set only on .quiz-bg leaves that header on plain white.
  // Paint the body itself so the theme background covers the entire page, edge to
  // edge — the USER wrapper intentionally skips doing this for the /game route.
  useEffect(() => {
    const body = document.body;
    if (backgroundImage) {
      body.style.backgroundImage = `url("${backgroundImage}")`;
      body.style.backgroundSize = "cover";
      body.style.backgroundPosition = "center center";
      body.style.backgroundRepeat = "no-repeat";
      body.style.backgroundAttachment = "fixed";
    }
    body.style.backgroundColor = "#0b2b8f";
    return () => {
      body.style.removeProperty("background-image");
      body.style.removeProperty("background-size");
      body.style.removeProperty("background-position");
      body.style.removeProperty("background-repeat");
      body.style.removeProperty("background-attachment");
      body.style.removeProperty("background-color");
    };
  }, [backgroundImage]);

  const rulesList = useMemo(() => {
    const val = themeData?.rules;
    const arr = ensureJsonArray(val);
    if (arr.length) return arr.map((r) => String(r).trim()).filter(Boolean);
    if (typeof val === "string" && val.trim()) {
      return val.split(/[\n|]/).map((r) => r.trim()).filter(Boolean);
    }
    return [];
  }, [themeData]);

  // Decorative twinkling star field (generated once, purely visual).
  const stars = useMemo(
    () =>
      Array.from({ length: 50 }, () => ({
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        size: Math.random() * 3 + 1,
        delay: `${Math.random() * 3}s`,
      })),
    []
  );

  // -------------------------------------------------------------------------
  // Fetch the full category-quiz question bank, then build this session's
  // playable list honoring categories_status / categoryQuestions / total_question.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!adminToken || !themeData || !currentThemeName) return;

    const load = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/getCategoryQuestions`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify({ organizationId, sessionId, currentTheme: currentThemeName }),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.message || "Failed to load questions");
        setRawQuestions(Array.isArray(data.questions) ? data.questions : []);
      } catch (error) {
        console.error("Failed to load questions:", error);
        setQuestionsError(error.message || "Failed to load questions");
      } finally {
        setLoadingQuestions(false);
      }
    };

    load();
  }, [adminToken, themeData, currentThemeName, organizationId, sessionId]);

  /** Pick this session's base question set (unshuffled) — grouped by category counts, or a flat cap. */
  const buildBaseSelection = useCallback(
    (pool) => {
      if (categoriesStatus && Object.keys(categoryQuestionCounts).length > 0) {
        const byCategory = {};
        for (const q of pool) {
          const cat = q.category || "";
          if (!byCategory[cat]) byCategory[cat] = [];
          byCategory[cat].push(q);
        }
        const picked = [];
        for (const [cat, count] of Object.entries(categoryQuestionCounts)) {
          const n = Math.max(0, Number(count) || 0);
          const available = byCategory[cat] || [];
          picked.push(...available.slice(0, n));
        }
        return picked.slice(0, totalQuestionTarget);
      }
      return pool.slice(0, totalQuestionTarget);
    },
    [categoriesStatus, categoryQuestionCounts, totalQuestionTarget]
  );

  // -------------------------------------------------------------------------
  // Start/resume the player's `stat` row. On resume, replay the exact question
  // order that was already persisted (stat.questions snapshot) starting from
  // question_count; on a fresh start, generate (and persist) a new order
  // honoring shuffle_questions.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!adminToken || !userId || progressLoaded || !themeData || loadingQuestions) return;
    if (!rawQuestions.length) return;

    const baseSelection = buildBaseSelection(rawQuestions);
    if (!baseSelection.length) {
      setQuestionsError("No questions are available for this theme yet.");
      return;
    }
    const freshOrder = shuffleQuestions ? shuffleArray(baseSelection) : baseSelection;

    const startGame = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/quizGameStart`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify({
            organizationId,
            sessionId,
            userId,
            email: storedUserData?.email || "",
            name: storedUserData?.name || "",
            user_type: storedUserData?.role || storedUserData?.source || "",
            questions: freshOrder.map((q) => q.id),
          }),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.message || "Failed to start game");

        if (data.reportId && !reportId) setReportId(data.reportId);

        const isDone = String(data.onboardingstage || "").toLowerCase() === "completed";
        if (isDone) {
          gameCompletedRef.current = true;
          setGameCompleted(true);
          try {
            sessionStorage.setItem(USER_DATA_KEY, JSON.stringify({ ...storedUserData, gameover: 1 }));
          } catch {
            /* ignore */
          }
          Swal.fire("Already Played", "You have already completed this game.", "info").then(() => {
            navigate("/leaderboard", { replace: true });
          });
          return;
        }

        // Resume in the exact order already persisted, if any.
        const snapshotIds = ensureJsonArray(data.questions);
        if (data.existing && snapshotIds.length > 0) {
          const byId = new Map(rawQuestions.map((q) => [String(q.id), q]));
          const resumedOrder = snapshotIds.map((id) => byId.get(String(id))).filter(Boolean);
          setPlayQuestions(resumedOrder.length ? resumedOrder : freshOrder);
        } else {
          setPlayQuestions(freshOrder);
        }

        setScore(Number(data.points) || 0);
        setElapsed(parseTimeToSeconds(data.time));
        setQuestionCount(Number(data.question_count) || 0);
        setProgressLoaded(true);
      } catch (error) {
        console.error("Failed to start game:", error);
        setQuestionsError(error.message || "Failed to start game");
      }
    };

    startGame();
  }, [
    adminToken,
    userId,
    organizationId,
    sessionId,
    progressLoaded,
    themeData,
    loadingQuestions,
    rawQuestions,
    buildBaseSelection,
    shuffleQuestions,
    storedUserData,
    reportId,
    navigate,
  ]);

  /** Push points + time to the external reports server, then persist the returned reportId. */
  const pushExternalReport = useCallback(
    async (currentScore, currentTime) => {
      const role = storedUserData?.role || storedUserData?.source;
      if (role === "demobypass" || role === "DEMO") return;
      if (!userId) return;

      try {
        const payload = {
          sessionId,
          organizationId,
          userId,
          role,
          token: storedUserData?.token,
          gameId: storedUserData?.gameId,
          name: storedUserData?.name || "",
          points: currentScore,
          time: formatTime(currentTime),
          reportId: reportId || undefined,
        };
        const response = await sendReport(payload);
        const newReportId = extractReportIdFromResponse(response);

        if (newReportId && newReportId !== reportId) {
          setReportId(newReportId);
          if (adminToken && userId) {
            try {
              await fetch(`${process.env.REACT_APP_BACKEND_URL}/quizGameLinkReport`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
                body: JSON.stringify({ organizationId, sessionId, userId, reportId: newReportId }),
              });
            } catch (linkErr) {
              console.error("Failed to link reportId:", linkErr);
            }
          }
          try {
            sessionStorage.setItem(USER_DATA_KEY, JSON.stringify({ ...storedUserData, reportId: newReportId }));
          } catch {
            /* ignore */
          }
        }
      } catch (error) {
        console.error("Failed to send external report:", error);
      }
    },
    [storedUserData, userId, sessionId, organizationId, reportId, adminToken]
  );

  const saveProgress = useCallback(
    async (count, currentScore, currentTime) => {
      if (gameCompletedRef.current || !adminToken || !userId) return;
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/quizGameProgress`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify({
            organizationId,
            sessionId,
            userId,
            time: formatTime(currentTime),
            points: currentScore,
            question_count: count,
          }),
        });
      } catch (error) {
        console.error("Failed to save progress:", error);
      }
    },
    [adminToken, userId, organizationId, sessionId]
  );

  const handleGameComplete = useCallback(
    async (finalCount, finalScore, finalTime) => {
      if (gameCompletedRef.current) return;
      gameCompletedRef.current = true;
      setGameCompleted(true);

      // Fire-and-forget: an external analytics push, not the leaderboard's
      // data source — no need to hold up the redirect on it.
      pushExternalReport(finalScore, finalTime);

      // This IS the leaderboard's data source (stat.points / stat.time), and
      // the leaderboard fetches immediately on mount — awaiting it here is
      // what stops that fetch from racing ahead of this write and showing
      // stale points/time for the last question.
      if (adminToken && userId) {
        try {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/quizGameComplete`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
            body: JSON.stringify({
              organizationId,
              sessionId,
              userId,
              time: formatTime(finalTime),
              points: finalScore,
              question_count: finalCount,
            }),
          });
        } catch (error) {
          console.error("Failed to complete game:", error);
        }
      }

      try {
        sessionStorage.setItem(USER_DATA_KEY, JSON.stringify({ ...storedUserData, gameover: 1 }));
      } catch {
        /* ignore */
      }

      navigate("/leaderboard", { replace: true });
    },
    [pushExternalReport, adminToken, organizationId, sessionId, userId, storedUserData, navigate]
  );

  // Save on unmount so an accidental tab-close/navigate doesn't lose current progress.
  useEffect(() => {
    return () => {
      if (!gameCompletedRef.current && progressLoaded) {
        const { questionCount: qc, score: sc, elapsed: el } = latestRef.current;
        saveProgress(qc, sc, el);
      }
    };
  }, [progressLoaded, saveProgress]);

  // Overall elapsed-time clock.
  useEffect(() => {
    if (loadingQuestions || gameCompleted || !progressLoaded) return undefined;
    const t = setInterval(() => setElapsed((p) => p + 1), 1000);
    return () => clearInterval(t);
  }, [loadingQuestions, gameCompleted, progressLoaded]);

  const handleSubmitRef = useRef(() => {});

  const handleSubmit = useCallback(
    (isTimeout = false) => {
      if (resolving || gameCompletedRef.current) return;
      const q = currentQuestion;
      if (!q) return;
      if (!isTimeout && selectedOptionId == null) {
        Swal.fire("Please select an option.", "", "warning");
        return;
      }

      setResolving(true);
      const isCorrect = !isTimeout && Number(selectedOptionId) === Number(q.answer_id);
      const delta = isCorrect ? rightPoints : -wrongPoints;
      const newScore = score + delta;
      const newCount = questionCount + 1;

      setScore(newScore);
      setQuestionCount(newCount);

      // Last question — skip the correct/incorrect reveal pause and leave
      // immediately instead of sitting on the game screen.
      if (newCount >= playQuestions.length) {
        handleGameComplete(newCount, newScore, elapsed);
        return;
      }

      setRevealState({
        question: q,
        correctId: Number(q.answer_id),
        wrongId: !isTimeout && !isCorrect ? Number(selectedOptionId) : null,
      });

      if (isCorrect) {
        setConfettiActive(true);
      } else {
        setWrongFlash(true);
        clearTimeout(wrongFlashTimerRef.current);
        wrongFlashTimerRef.current = setTimeout(() => setWrongFlash(false), 680);
      }

      setTimeout(() => {
        setSelectedOptionId(null);
        setRevealState(null);
        setResolving(false);
        saveProgress(newCount, newScore, elapsed);
      }, 1000);
    },
    [
      resolving,
      currentQuestion,
      selectedOptionId,
      rightPoints,
      wrongPoints,
      score,
      questionCount,
      elapsed,
      playQuestions.length,
      handleGameComplete,
      saveProgress,
    ]
  );

  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  // Per-question countdown (persists across refresh via sessionStorage, same as the reference UI).
  const timerStorageKey = useCallback(
    (idx) => `dyk_q_timer_${userId}_${organizationId}_${sessionId}_${idx}`,
    [userId, organizationId, sessionId]
  );

  const hintOpen = hintConfirmOpen || hintDisplayOpen;
  const hintPauseStartRef = useRef(null);

  // Freeze the countdown while a hint dialog is open. The countdown itself is
  // deadline-based (see below), so "pausing" it means pushing the stored
  // deadline forward by however long the dialog stayed open.
  useEffect(() => {
    if (!useTimeout) return undefined;
    if (hintOpen) {
      hintPauseStartRef.current = Date.now();
      return undefined;
    }
    const pausedSince = hintPauseStartRef.current;
    hintPauseStartRef.current = null;
    if (pausedSince == null) return undefined;
    const pausedMs = Date.now() - pausedSince;
    if (pausedMs <= 0) return undefined;
    const key = timerStorageKey(currentIndex);
    try {
      const raw = sessionStorage.getItem(key);
      const saved = raw ? JSON.parse(raw) : null;
      if (saved && Number(saved.until)) {
        sessionStorage.setItem(key, JSON.stringify({ until: Number(saved.until) + pausedMs }));
      }
    } catch {
      /* ignore */
    }
    return undefined;
  }, [hintOpen, useTimeout, currentIndex, timerStorageKey]);

  useEffect(() => {
    if (!useTimeout || resolving || gameCompletedRef.current || hintOpen) return undefined;
    if (!progressLoaded || currentIndex >= playQuestions.length) return undefined;

    const key = timerStorageKey(currentIndex);
    let deadline;
    try {
      const raw = sessionStorage.getItem(key);
      const saved = raw ? JSON.parse(raw) : null;
      deadline = saved && Number(saved.until) > Date.now() ? Number(saved.until) : Date.now() + questionDurationSeconds * 1000;
    } catch {
      deadline = Date.now() + questionDurationSeconds * 1000;
    }
    try {
      sessionStorage.setItem(key, JSON.stringify({ until: deadline }));
    } catch {
      /* ignore */
    }

    const tick = () => {
      const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setQuestionSecondsLeft(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        handleSubmitRef.current(true);
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [useTimeout, resolving, hintOpen, progressLoaded, currentIndex, playQuestions.length, questionDurationSeconds, timerStorageKey]);

  useEffect(() => {
    if (!useTimeout) setQuestionSecondsLeft(questionDurationSeconds);
  }, [useTimeout, questionDurationSeconds]);

  const handleSelectOption = (optionId) => {
    if (resolving || revealState) return;
    setSelectedOptionId(optionId);
  };

  const handleHintClick = () => {
    if (resolving || !currentQuestion) return;
    if (revealedHints[currentIndex]) {
      setHintDisplayText(String(currentQuestion.correct_answer || ""));
      setHintDisplayOpen(true);
      return;
    }
    if (hintsLeft > 0) {
      setHintConfirmOpen(true);
    } else {
      Swal.fire("No hints left!", "You have used all your hints.", "warning");
    }
  };

  const confirmUseHint = () => {
    setHintConfirmOpen(false);
    setHintsLeft((h) => h - 1);
    setRevealedHints((prev) => ({ ...prev, [currentIndex]: true }));
    setHintDisplayText(String(currentQuestion?.correct_answer || ""));
    setHintDisplayOpen(true);
  };

  const progressPct = playQuestions.length ? (currentIndex / playQuestions.length) * 100 : 0;

  if (loadingQuestions || !progressLoaded) {
    return (
      <div className="quiz-bg game-loading" style={themeVars}>
        <p>{questionsError || "Loading questions..."}</p>
      </div>
    );
  }

  if (questionsError && !playQuestions.length) {
    return (
      <div className="quiz-bg game-loading" style={themeVars}>
        <p>{questionsError}</p>
      </div>
    );
  }

  // While a question's reveal (correct/incorrect highlight) is showing, keep rendering
  // the question that was just answered — `currentQuestion` has already advanced past it
  // by then (it's derived from questionCount), which would otherwise blank the screen.
  const displayQuestion = revealState?.question || currentQuestion;

  return (
    <div className="quiz-bg" style={themeVars}>
      {confettiActive && <ConfettiBurst onDone={() => setConfettiActive(false)} />}

      <div className="stars" aria-hidden="true">
        {stars.map((star, i) => (
          <div
            key={i}
            className="star"
            style={{ left: star.left, top: star.top, width: star.size, height: star.size, animationDelay: star.delay }}
          />
        ))}
      </div>

      <div className={`quiz-wrong-feedback${wrongFlash ? " is-visible" : ""}`} aria-hidden="true">
        <div className="quiz-wrong-feedback-inner">&#10005;</div>
      </div>

      {rulesOpen && (
        <div className="rules-popup-overlay is-open" role="presentation" onClick={() => setRulesOpen(false)}>
          <div
            className="rules-popup-dialog"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="rules-popup-close" aria-label="Close rules" onClick={() => setRulesOpen(false)}>
              &times;
            </button>
            <div className="rules-popup-header">
              <h2 className="rules-popup-title">Rules</h2>
            </div>
            <div className="rules-popup-inner">
              <div className="rules-popup-content-box">
                <div className="rules-popup-scroll">
                  {rulesList.length > 0 ? (
                    <ul className="rules-popup-list">
                      {rulesList.map((rule, i) => (
                        <li key={i}>{rule}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="rules-popup-empty">No rules are set for this game.</p>
                  )}
                </div>
              </div>
              <div className="hint-popup-actions">
                <button type="button" className="quiz-plain-btn" onClick={() => setRulesOpen(false)}>
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {hintConfirmOpen && (
        <div className="hint-confirm-overlay is-open" role="presentation" onClick={() => setHintConfirmOpen(false)}>
          <div className="hint-confirm-dialog" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <p>
              Use one hint? You have <span>{hintsLeft}</span> hints left.
            </p>
            <div className="hint-confirm-actions">
              <button type="button" className="hint-confirm-btn hint-confirm-btn--no" onClick={() => setHintConfirmOpen(false)}>
                No
              </button>
              <button type="button" className="hint-confirm-btn hint-confirm-btn--yes" onClick={confirmUseHint}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {hintDisplayOpen && (
        <div className="hint-popup-overlay is-open" role="presentation" onClick={() => setHintDisplayOpen(false)}>
          <div className="hint-popup-dialog" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="hint-popup-close" aria-label="Close" onClick={() => setHintDisplayOpen(false)}>
              &times;
            </button>
            <div className="hint-popup-header">
              <img src="/img/hint/hintittle.png" alt="Need a hint?" className="hint-popup-title-img" />
            </div>
            <div className="hint-popup-inner">
              <div className="hint-popup-content-box">
                <span className="hint-popup-star" aria-hidden="true">
                  &#9733;
                </span>
                <p className="hint-popup-text">{hintDisplayText}</p>
              </div>
              <div className="hint-popup-actions">
                <button type="button" className="hint-popup-gotit" aria-label="Got it" onClick={() => setHintDisplayOpen(false)}>
                  <img src="/img/hint/gotit.png" alt="Got it!" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="quiz-container">
        <div className="quiz-header">
          <div className="timer-section">
            <div className="timer-box">
              <span className="stat-box-inner">
                <span className="timer-icon" aria-hidden="true">
                  <i className="fa-solid fa-clock" />
                </span>
                <span className="timer-text">
                  {useTimeout ? `${questionSecondsLeft}s TIME LEFT` : formatTime(elapsed)}
                </span>
              </span>
            </div>
          </div>

          <div className="progress-section">
            <div className="question-number">
              Question {Math.min(currentIndex + 1, playQuestions.length)}/{playQuestions.length}
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progressPct}%` }} />
            </div>
            {categoriesStatus && displayQuestion?.category && (
              <div className="question-category">Category : {displayQuestion.category}</div>
            )}
          </div>

          <div className="points-section">
            <div className="points-rules-row">
              <div className="points-box">
                <span className="stat-box-inner">
                  <span className="star-icon" aria-hidden="true">
                    <i className="fa-solid fa-star" />
                  </span>
                  <span className="points-text">{score} POINTS</span>
                </span>
              </div>
              <button
                type="button"
                className="rules-help-button"
                aria-haspopup="dialog"
                aria-label="View game rules"
                title="Rules"
                onClick={() => setRulesOpen(true)}
              >
                <i className="fa-solid fa-question" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>

        <div className="question-main-area">
          <div className="question-section">
            <div className="question-row">
              <div className="question-area">
                <div className="question-box">
                  <div className="question-text">{displayQuestion?.question_name}</div>
                </div>
              </div>
              {hintsEnabled && (
                <div className="hint-button" role="button" tabIndex={0} aria-label="Hint" onClick={handleHintClick}>
                  <img src="/img/hint/hint.png" alt="Hint" className="hint-button-img" />
                </div>
              )}
            </div>

            {displayQuestion?.question_hint && (
              <div className="question-hint-media">
                {getQuestionHintMediaKind(displayQuestion.question_hint) === "video" ? (
                  <video
                    key={displayQuestion.question_hint}
                    className="question-hint-media-el"
                    src={getDvSourceImageUrl(displayQuestion.question_hint)}
                    controls
                    playsInline
                  />
                ) : getQuestionHintMediaKind(displayQuestion.question_hint) === "audio" ? (
                  <audio
                    key={displayQuestion.question_hint}
                    className="question-hint-media-el question-hint-media-el--audio"
                    src={getDvSourceImageUrl(displayQuestion.question_hint)}
                    controls
                  />
                ) : (
                  <img
                    className="question-hint-media-el"
                    src={getDvSourceImageUrl(displayQuestion.question_hint)}
                    alt=""
                  />
                )}
              </div>
            )}
          </div>

          <div className="answer-options">
            {[0, 1].map((rowIdx) => (
              <div className="option-row" key={rowIdx}>
                {[0, 1].map((colIdx) => {
                  const idx = rowIdx * 2 + colIdx;
                  const key = OPTION_KEYS[idx];
                  const optionId = idx + 1;
                  const text = displayQuestion?.[key];
                  if (!displayQuestion || !text) return null;
                  const isSelected = selectedOptionId === optionId;
                  const isCorrectReveal = revealState && revealState.correctId === optionId;
                  const isWrongReveal = revealState && revealState.wrongId === optionId;
                  const cls = [
                    "answer-option",
                    isSelected && !revealState ? "selected" : "",
                    isCorrectReveal ? "correct" : "",
                    isWrongReveal ? "incorrect" : "",
                  ]
                    .filter(Boolean)
                    .join(" ");
                  return (
                    <div key={key} className={cls} onClick={() => handleSelectOption(optionId)}>
                      <span className="option-letter">{OPTION_LETTERS[idx]}</span>
                      <span className="option-text">{text}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="submit-container">
            <button type="button" className="submit-button" disabled={resolving} onClick={() => handleSubmit(false)}>
              <span className="submit-button-label">Submit</span>
            </button>
          </div>

          {hintsEnabled && (
            <div className="quiz-footer">
              <div className="hints-left">
                <span className="stat-box-inner">
                  <span className="shield-icon" aria-hidden="true">
                    <i className="fa-solid fa-shield-halved" />
                  </span>
                  <span className="hints-text">{hintsLeft} HINTS LEFT</span>
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
